// globalnav.js - Complete corrected file with event data passing to registration
import React, { useState, useCallback, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import toast from "react-hot-toast";

import {
  RiMenuLine,
  RiCloseLine,
  RiLoader4Line,
  RiCheckLine,
  RiTeamLine,
  RiCalendarLine,
  RiCalendarCheckLine,
  RiUploadLine,
  RiFileListLine,
  RiShieldStarLine,
  RiInformationLine,
  RiMapPinLine,
  RiArrowDownSLine,
  RiShieldCheckLine,
  RiCalendarEventLine,
  RiArticleLine,
  RiFileTextLine,
  RiSpeakerLine,
  RiCalendar2Line,
  RiDownloadLine,
  RiFileCopyLine,
  RiBookOpenLine,
  RiFileTextFill,
  RiPresentationLine,
  RiUser3Line,
  RiAwardLine,
  RiExternalLinkLine,
  RiEyeLine,
  RiArrowRightLine,
  RiHomeLine,          // added
  RiMailLine,          // added
  RiHandHeartLine,     // added
  RiGroupLine,         // added
} from "react-icons/ri";

import { API_BASE } from "../config";
import logo from "../assets/logo.png";

const T = {
  white: "#FFFFFF",
  offWhite: "#F4F7FF",
  softBlue: "#EBF1FF",
  primary: "#1A56DB",
  primaryDark: "#1240A8",
  primaryLight: "#3B82F6",
  navy: "#0F2557",
  navyDeep: "#091A3E",
  accent: "#60A5FA",
  accentGold: "#F59E0B",
  text: "#0F1F3D",
  textMid: "#374966",
  textMuted: "#6B7FA3",
  border: "#D6E4FF",
  borderLight: "#EBF1FF",
  shadow: "0 4px 24px rgba(26,86,219,0.08)",
  shadowMd: "0 8px 40px rgba(26,86,219,0.12)",
  shadowLg: "0 20px 60px rgba(26,86,219,0.16)",
  gradPrimary: "linear-gradient(135deg, #1A56DB 0%, #3B82F6 100%)",
  gradNavy: "linear-gradient(135deg, #0F2557 0%, #1A56DB 100%)",
  gradSoft: "linear-gradient(135deg, #EBF1FF 0%, #F4F7FF 100%)",
  gradGold: "linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)",
};

/* ─────────────────────────────────────────────────────────────
   THEME
───────────────────────────────────────────────────────────── */
const NAV_BG = "#ffffff";
const ACCENT = "#0E7C86";
const GOLD = "#F0C040";
const TEXT_DIM = "#000000";
const TEXT_HOV = "#000000";
const BORDER = "rgba(0,0,0,0.08)";

/* ─── RESPONSIVE STYLES (injected via <style>) ────────────── */
const NAVBAR_CSS = `
  /* Hide desktop menu & show hamburger on tablets and below */
  @media (max-width: 1024px) {
    .gnav-desktop {
      display: none !important;
    }
    .gnav-hamburger {
      display: flex !important;
    }
  }
  /* Hide hamburger on large screens */
  @media (min-width: 1025px) {
    .gnav-hamburger {
      display: none !important;
    }
  }
  /* Mobile menu overlay styling */
  .gnav-mobile-menu {
    position: fixed;
    top: 80px;
    right: 0;
    bottom: 0;
    width: 100%;
    max-width: 320px;
    background: #ffffff;
    box-shadow: -4px 0 20px rgba(0,0,0,0.1);
    z-index: 999;
    overflow-y: auto;
    padding: 16px 0;
    border-left: 1px solid rgba(0,0,0,0.08);
    transition: transform 0.3s ease;
  }
  @media (max-width: 480px) {
    .gnav-mobile-menu {
      max-width: 100%;
      top: 70px; /* adjust if navbar height changes */
    }
    .gnav-mobile-menu .nav-link {
      font-size: 14px !important;
      padding: 12px 16px !important;
    }
    .gnav-mobile-menu .nav-link-indent {
      padding-left: 28px !important;
      font-size: 13px !important;
    }
    .gnav-mobile-menu .section-label {
      font-size: 10px !important;
      padding: 8px 16px 2px !important;
    }
  }
  /* Additional touch-friendly adjustments */
  .gnav-dropdown-trigger {
    cursor: pointer;
  }
`;

/* ─────────────────────────────────────────────────────────────
   HELPER FUNCTION TO CREATE SLUG FROM EVENT TITLE
───────────────────────────────────────────────────────────── */
const createEventSlug = (event) => {
  if (!event) return '';
  if (event.slug) return event.slug;
  if (!event.title) return '';
  return event.title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/--+/g, '-')
    .trim();
};

/* ─────────────────────────────────────────────────────────────
   DROPDOWN SHELL
───────────────────────────────────────────────────────────── */
function DropdownShell({ trigger, children, minWidth = 220 }) {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const show = () => {
    clearTimeout(timer.current);
    setOpen(true);
  };
  const hide = () => {
    timer.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <div
      style={{ position: "relative", display: "inline-block" }}
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      {trigger(open)}
      <div
        onMouseEnter={show}
        onMouseLeave={hide}
        style={{
          position: "absolute",
          top: "calc(100% + 8px)",
          left: 0,
          zIndex: 9999,
          minWidth,
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transform: open
            ? "translateY(0) scale(1)"
            : "translateY(-8px) scale(0.97)",
          transition: "all 0.15s ease",
          background: NAV_BG,
          border: `1px solid ${BORDER}`,
          borderTop: `3px solid ${ACCENT}`,
          borderRadius: 10,
          boxShadow: "0 20px 50px rgba(0,0,0,0.12)",
        }}
      >
        {children}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   TRIGGER BUTTON
───────────────────────────────────────────────────────────── */
function TriggerBtn({ icon: Icon, label, open, isActive }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "8px 14px",
        borderRadius: 6,
        border: "none",
        cursor: "pointer",
        fontFamily: '"Source Sans 3", sans-serif',
        fontSize: 14,
        fontWeight: 600,
        whiteSpace: "nowrap",
        color: isActive ? "#fff" : hover ? TEXT_HOV : TEXT_DIM,
        background: isActive
          ? ACCENT
          : hover
            ? "rgba(0,0,0,0.04)"
            : "transparent",
      }}
    >
      {Icon && <Icon style={{ fontSize: 15 }} />}
      {label}
      <RiArrowDownSLine
        style={{
          fontSize: 16,
          transition: "0.2s",
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
        }}
      />
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────
   NAV LINK (with optional icon)
───────────────────────────────────────────────────────────── */
function NavLink({ path, label, icon: Icon, isActive }) {
  const [hover, setHover] = useState(false);
  return (
    <Link
      to={path}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "8px 14px",
        borderRadius: 6,
        textDecoration: "none",
        fontFamily: '"Source Sans 3", sans-serif',
        fontSize: 14,
        fontWeight: 600,
        whiteSpace: "nowrap",
        color: isActive ? "#fff" : hover ? TEXT_HOV : TEXT_DIM,
        background: isActive
          ? ACCENT
          : hover
            ? "rgba(0,0,0,0.04)"
            : "transparent",
      }}
    >
      {Icon && <Icon style={{ fontSize: 15 }} />}
      {label}
    </Link>
  );
}

/* ─────────────────────────────────────────────────────────────
   DROP ITEM
───────────────────────────────────────────────────────────── */
function DropItem({ path, label, icon: Icon, description, active, external }) {
  const [hover, setHover] = useState(false);
  const content = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 16px",
        textDecoration: "none",
        borderLeft: active ? `3px solid ${ACCENT}` : "3px solid transparent",
        background: active
          ? "rgba(14,124,134,0.08)"
          : hover
            ? "rgba(0,0,0,0.03)"
            : "transparent",
        transition: "all 0.15s ease",
      }}
    >
      {Icon && <Icon style={{ fontSize: 15, color: active ? ACCENT : "#000000" }} />}
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#000000" }}>{label}</div>
        {description && <div style={{ fontSize: 11, color: "#555", marginTop: 1 }}>{description}</div>}
      </div>
      {external && <RiExternalLinkLine size={12} style={{ color: ACCENT, opacity: 0.7 }} />}
    </div>
  );

  if (external) {
    return (
      <a
        href={path}
        target="_blank"
        rel="noopener noreferrer"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{ textDecoration: "none", display: "block" }}
      >
        {content}
      </a>
    );
  }

  return (
    <Link
      to={path}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ textDecoration: "none", display: "block" }}
    >
      {content}
    </Link>
  );
}

/* ─────────────────────────────────────────────────────────────
   DROP ITEM WITH STATE - For passing state to Links
───────────────────────────────────────────────────────────── */
function DropItemWithState({ path, label, icon: Icon, description, active, external, state }) {
  const [hover, setHover] = useState(false);
  const content = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 16px",
        textDecoration: "none",
        borderLeft: active ? `3px solid ${ACCENT}` : "3px solid transparent",
        background: active
          ? "rgba(14,124,134,0.08)"
          : hover
            ? "rgba(0,0,0,0.03)"
            : "transparent",
        transition: "all 0.15s ease",
      }}
    >
      {Icon && <Icon style={{ fontSize: 15, color: active ? ACCENT : "#000000" }} />}
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#000000" }}>{label}</div>
        {description && <div style={{ fontSize: 11, color: "#555", marginTop: 1 }}>{description}</div>}
      </div>
      {external && <RiExternalLinkLine size={12} style={{ color: ACCENT, opacity: 0.7 }} />}
    </div>
  );

  if (external) {
    return (
      <a
        href={path}
        target="_blank"
        rel="noopener noreferrer"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{ textDecoration: "none", display: "block" }}
      >
        {content}
      </a>
    );
  }

  return (
    <Link
      to={path}
      state={state}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ textDecoration: "none", display: "block" }}
    >
      {content}
    </Link>
  );
}

/* ─────────────────────────────────────────────────────────────
   POLICY DROPDOWN
───────────────────────────────────────────────────────────── */
function MainPolicyDropdown() {
  const location = useLocation();
  const isActive =
    location.pathname === "/registration-policies" ||
    location.pathname === "/privacy-policy";
  return (
    <DropdownShell
      minWidth={240}
      trigger={(open) => (
        <TriggerBtn
          icon={RiFileTextFill}
          label="Policy"
          open={open}
          isActive={isActive}
        />
      )}
    >
      <div style={{ padding: "6px 0" }}>
        <DropItem
          path="/registration-policies"
          label="Registration Policy"
          icon={RiFileListLine}
          description="View registration policies"
          active={location.pathname === "/registration-policies"}
        />
        <DropItem
          path="/privacy-policy"
          label="Privacy Policy"
          icon={RiShieldStarLine}
          description="Privacy information"
          active={location.pathname === "/privacy-policy"}
        />
      </div>
    </DropdownShell>
  );
}

/* ─────────────────────────────────────────────────────────────
   PROGRAM DROPDOWN
───────────────────────────────────────────────────────────── */
function ProgramDropdown({ summitId, summitSlug }) {
  const location = useLocation();
  const [downloading, setDownloading] = useState(false);
  const [hasActiveSchedule, setHasActiveSchedule] = useState(false);
  const [checkingSchedule, setCheckingSchedule] = useState(true);
  const [scheduleData, setScheduleData] = useState(null);
  const base = `/event/${summitSlug}`;

  const isActive =
    location.pathname === `${base}/programme-overview` ||
    location.pathname === `${base}/speakers` ||
    location.pathname === `${base}/schedule` ||
    location.pathname === `${base}/presentation-guidelines` ||
    location.pathname === `${base}/download-pdf`;

  useEffect(() => {
    const check = async () => {
      try {
        setCheckingSchedule(true);
        const token = localStorage.getItem("token");
        let r = await axios.get(`${API_BASE}/api/pdf-schedule/active`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { eventId: summitId },
        });
        if (!r.data.success || !r.data.data) {
          r = await axios.get(`${API_BASE}/api/pdf-schedule/active`, {
            headers: { Authorization: `Bearer ${token}` },
          });
        }
        if (r.data.success && r.data.data) {
          setHasActiveSchedule(true);
          setScheduleData(r.data.data);
        } else {
          setHasActiveSchedule(false);
          setScheduleData(null);
        }
      } catch {
        setHasActiveSchedule(false);
        setScheduleData(null);
      } finally {
        setCheckingSchedule(false);
      }
    };
    check();
  }, [summitId]);

  const handleDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDownloading(true);
    try {
      const token = localStorage.getItem("token");
      let sr = await axios.get(`${API_BASE}/api/pdf-schedule/active`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { eventId: summitId },
      });
      if (!sr.data.success || !sr.data.data) {
        sr = await axios.get(`${API_BASE}/api/pdf-schedule/active`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      if (sr.data.success && sr.data.data) {
        const sched = sr.data.data;
        const pdfR = await axios.post(
          `${API_BASE}/api/pdf-schedule/generate-pdf`,
          { scheduleId: sched._id, pdfType: "program", eventId: summitId },
          { responseType: "blob", headers: { Authorization: `Bearer ${token}` } }
        );
        const url = window.URL.createObjectURL(
          new Blob([pdfR.data], { type: "application/pdf" })
        );
        const a = document.createElement("a");
        a.href = url;
        const edition = sched.edition || '2026';
        a.download = `schedule-${edition}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Schedule PDF downloaded successfully!");
      } else {
        toast.error("No active schedule found for this event. Please contact the administrator.");
      }
    } catch (err) {
      console.error('Download error:', err);
      if (err.response?.status === 404) {
        toast.error("No schedule available for download for this event.");
      } else if (err.response?.status === 401) {
        toast.error("Authentication required. Please login again.");
      } else {
        toast.error(err.response?.data?.message || "Error generating PDF. Please try again.");
      }
    } finally {
      setDownloading(false);
    }
  };

  return (
    <DropdownShell
      minWidth={260}
      trigger={(open) => (
        <TriggerBtn
          icon={RiCalendarEventLine}
          label="Program"
          open={open}
          isActive={isActive}
        />
      )}
    >
      <div style={{ padding: "6px 0" }}>
        <DropItem
          path={`${base}/speakers`}
          label="Speakers"
          icon={RiSpeakerLine}
          description="View summit speakers"
          active={location.pathname === `${base}/speakers`}
        />
        <DropItem
          path={`${base}/programme-overview`}
          label="Preliminary Program"
          icon={RiCalendarEventLine}
          description="View summit program overview"
          active={location.pathname === `${base}/programme-overview`}
        />
        <DropItem
          path={`${base}/schedule`}
          label="Schedule"
          icon={RiCalendarLine}
          description="View summit schedule"
          active={location.pathname === `${base}/schedule`}
        />
        <DropItem
          path={`${base}/presentation-guidelines`}
          label="Presentation Guidelines"
          icon={RiPresentationLine}
          description="Guidelines for presenters"
          active={location.pathname === `${base}/presentation-guidelines`}
        />
        <div
          onClick={hasActiveSchedule && !downloading ? handleDownload : undefined}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "10px 16px",
            cursor: hasActiveSchedule && !downloading ? "pointer" : "not-allowed",
            opacity: !hasActiveSchedule || downloading ? 0.55 : 1,
            borderLeft: "3px solid transparent",
            background: "transparent",
            transition: "all 0.15s",
          }}
          onMouseEnter={(e) => {
            if (hasActiveSchedule && !downloading) {
              e.currentTarget.style.background = "rgba(0,0,0,0.03)";
              e.currentTarget.style.borderLeftColor = ACCENT;
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.borderLeftColor = "transparent";
          }}
        >
          {downloading ? (
            <RiLoader4Line
              style={{
                fontSize: 15,
                color: ACCENT,
                animation: "spin 1s linear infinite",
              }}
            />
          ) : (
            <RiDownloadLine
              style={{
                fontSize: 15,
                color: !hasActiveSchedule ? "#94A3B8" : "#000",
              }}
            />
          )}
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: !hasActiveSchedule ? "#94A3B8" : "#000" }}>
              {downloading ? "Generating…" : "Download Program"}
            </div>
            <div style={{ fontSize: 11, color: !hasActiveSchedule ? "#CBD5E1" : "#555", marginTop: 1 }}>
              {checkingSchedule
                ? "Checking…"
                : hasActiveSchedule
                  ? `${scheduleData?.schedule?.length || 0} sessions available`
                  : "No schedule available"}
            </div>
          </div>
        </div>
      </div>
      <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
    </DropdownShell>
  );
}

/* ─────────────────────────────────────────────────────────────
   SUBMISSION DROPDOWN
───────────────────────────────────────────────────────────── */
function SubmissionDropdown({ summitId, summitSlug }) {
  const location = useLocation();
  const base = `/event/${summitSlug}`;
  const isActive =
    location.pathname === `${base}/topics` ||
    location.pathname === `${base}/submission-guidelines` ||
    location.pathname === `${base}/abstract-submission`;

  return (
    <DropdownShell
      minWidth={240}
      trigger={(open) => (
        <TriggerBtn
          icon={RiUploadLine}
          label="Submission"
          open={open}
          isActive={isActive}
        />
      )}
    >
      <div style={{ padding: "6px 0" }}>
        <DropItem
          path={`${base}/topics`}
          label="Topics"
          icon={RiArticleLine}
          description="View all topics"
          active={location.pathname === `${base}/topics`}
        />
        <DropItem
          path={`${base}/submission-guidelines`}
          label="Submission Guidelines"
          icon={RiFileCopyLine}
          description="View submission guidelines"
          active={location.pathname === `${base}/submission-guidelines`}
        />
        <DropItem
          path={`${base}/abstract-submission`}
          label="Submit Abstract"
          icon={RiUploadLine}
          description="Submit your abstract"
          active={location.pathname === `${base}/abstract-submission`}
        />
      </div>
    </DropdownShell>
  );
}

/* ─────────────────────────────────────────────────────────────
   REGISTRATION DROPDOWN - Updated to use slug in URL
───────────────────────────────────────────────────────────── */
function RegistrationDropdown({ summitId, summitSlug }) {
  const location = useLocation();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const base = `/event/${summitSlug}`;
  const isActive =
    location.pathname === `${base}/registration-fee` ||
    location.pathname === `${base}/registration-policies` ||
    location.pathname === `${base}/register`;

  useEffect(() => {
    const fetchEvent = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        let response;
        let data;
        try {
          response = await fetch(`${API_BASE}/api/events/${summitId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          data = await response.json();
        } catch (e) {
          console.log('Fetch by ID failed, trying by slug:', e);
        }
        if (!data?.success && !data?.event) {
          response = await fetch(`${API_BASE}/api/events/slug/${summitSlug}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          data = await response.json();
        }
        if (data?.success && data?.event) {
          setEvent(data.event);
        } else if (data?.event) {
          setEvent(data.event);
        } else {
          setEvent({
            _id: summitId,
            title: summitSlug || 'Event',
            slug: summitSlug
          });
        }
      } catch (error) {
        console.error('Error fetching event:', error);
        setEvent({
          _id: summitId,
          title: summitSlug || 'Event',
          slug: summitSlug
        });
      } finally {
        setLoading(false);
      }
    };
    if (summitId) {
      fetchEvent();
    }
  }, [summitId, summitSlug]);

  const eventTitle = event?.title || summitSlug || 'Event';
  const eventSlug = event?.slug || summitSlug;

  return (
    <DropdownShell
      minWidth={240}
      trigger={(open) => (
        <TriggerBtn
          icon={RiFileListLine}
          label="Registration"
          open={open}
          isActive={isActive}
        />
      )}
    >
      <div style={{ padding: "6px 0" }}>
        <DropItem
          path={`${base}/registration-fee`}
          label="Registration Fee"
          icon={RiFileListLine}
          description="View registration fees"
          active={location.pathname === `${base}/registration-fee`}
        />
        <DropItem
          path={`${base}/registration-policies`}
          label="Registration Policies"
          icon={RiShieldCheckLine}
          description="View registration policies"
          active={location.pathname === `${base}/registration-policies`}
        />
        <DropItem
          path={`/event/${eventSlug}/register`}
          label="Online Registration"
          icon={RiCheckLine}
          description="Register online"
          active={location.pathname === `${base}/register`}
        />
      </div>
    </DropdownShell>
  );
}

/* ─────────────────────────────────────────────────────────────
   INFORMATION DROPDOWN (with Awards added)
───────────────────────────────────────────────────────────── */
function InformationDropdown({ summitId, summitSlug }) {
  const location = useLocation();
  const base = `/event/${summitSlug}`;
  const isActive =
    location.pathname === `${base}/venue` ||
    location.pathname === `${base}/code-of-conduct` ||
    location.pathname === `${base}/privacy-policy` ||
    location.pathname === `${base}/awards` ||
    location.pathname === `${base}/summit-publications`;

  return (
    <DropdownShell
      minWidth={240}
      trigger={(open) => (
        <TriggerBtn
          icon={RiInformationLine}
          label="Information"
          open={open}
          isActive={isActive}
        />
      )}
    >
      <div style={{ padding: "6px 0" }}>
        <DropItem
          path={`${base}/venue`}
          label="Venue and Information"
          icon={RiMapPinLine}
          description="Location & directions"
          active={location.pathname === `${base}/venue`}
        />
        <DropItem
          path={`${base}/awards`}
          label="Awards"
          icon={RiAwardLine}
          description="View awards and recognition"
          active={location.pathname === `${base}/awards`}
        />
        <DropItem
          path={`${base}/code-of-conduct`}
          label="Code of Conduct"
          icon={RiShieldCheckLine}
          description="Community guidelines"
          active={location.pathname === `${base}/code-of-conduct`}
        />
        <DropItem
          path={`${base}/privacy-policy`}
          label="Privacy Policy"
          icon={RiShieldStarLine}
          description="Privacy information"
          active={location.pathname === `${base}/privacy-policy`}
        />
        <DropItem
          path={`${base}/summit-publications`}
          label="Publications"
          icon={RiBookOpenLine}
          description="View summit publications"
          active={location.pathname === `${base}/summit-publications`}
        />
      </div>
    </DropdownShell>
  );
}

/* ─────────────────────────────────────────────────────────────
   MEETINGS DROPDOWN (with "View All Events" button under Completed)
───────────────────────────────────────────────────────────── */
function MeetingsDropdown() {
  const location = useLocation();
  const [subMenu, setSubMenu] = useState("upcoming");
  const [events, setEvents] = useState({ upcoming: [], completed: [] });
  const [loading, setLoading] = useState({ upcoming: false, completed: false });
  const [error, setError] = useState({ upcoming: null, completed: null });

  const fetchEvents = useCallback(
    async (status) => {
      if (status === "all") return;
      if (loading[status]) return;
      setLoading((p) => ({ ...p, [status]: true }));
      setError((p) => ({ ...p, [status]: null }));
      try {
        const token = localStorage.getItem("token");
        const r = await fetch(`${API_BASE}/api/events?status=${status}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const d = await r.json();
        let arr =
          d.events && Array.isArray(d.events)
            ? d.events
            : d.success && d.events
              ? d.events
              : Array.isArray(d)
                ? d
                : d.data && Array.isArray(d.data)
                  ? d.data
                  : [];
        if (arr.length > 0) {
          setEvents((p) => ({ ...p, [status]: arr }));
        } else {
          setEvents((p) => ({ ...p, [status]: [] }));
          setError((p) => ({ ...p, [status]: `No ${status} events found` }));
        }
      } catch (err) {
        console.error(`❌ Error fetching ${status} events:`, err);
        setError((p) => ({
          ...p,
          [status]: "Network error. Please try again.",
        }));
        setEvents((p) => ({ ...p, [status]: [] }));
      } finally {
        setLoading((p) => ({ ...p, [status]: false }));
      }
    },
    [loading],
  );

  useEffect(() => {
    if (subMenu !== "all" && events[subMenu]?.length === 0 && !loading[subMenu] && !error[subMenu]) {
      fetchEvents(subMenu);
    }
  }, [subMenu, fetchEvents, events, loading, error]);

  const handleEventClick = (eventSlug) => {
    const url = `/event/${eventSlug}`;
    window.open(url, '_blank');
  };

  return (
    <DropdownShell
      minWidth={500}
      trigger={(open) => (
        <TriggerBtn
          icon={RiCalendarEventLine}
          label="Meetings"
          open={open}
          isActive={false}
        />
      )}
    >
      <div style={{ display: "flex", minWidth: 500 }}>
        <div
          style={{
            minWidth: 160,
            padding: "6px 0",
            borderRight: `1px solid ${T.borderLight}`,
          }}
        >
          {[
            { key: "upcoming", label: "Upcoming Events", icon: RiCalendarCheckLine },
            { key: "completed", label: "Completed", icon: RiCheckLine },
            { key: "all", label: "All Events", icon: RiEyeLine },
          ].map((item) => (
            <div
              key={item.key}
              onClick={() => setSubMenu(item.key)}
              style={{
                padding: "10px 16px",
                cursor: "pointer",
                background: subMenu === item.key ? `${T.primary}08` : "transparent",
                borderLeft: subMenu === item.key ? `3px solid ${T.primary}` : "3px solid transparent",
                transition: "all 0.2s ease",
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: subMenu === item.key ? T.primary : T.text,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <item.icon size={14} />
                {item.label}
              </div>
            </div>
          ))}
        </div>

        <div style={{ width: 340, maxHeight: 400, overflowY: "auto" }}>
          {subMenu !== "all" && loading[subMenu] ? (
            <div style={{ padding: 30, textAlign: "center" }}>
              <RiLoader4Line
                style={{
                  fontSize: 24,
                  color: T.primary,
                  animation: "spin 1s linear infinite",
                  marginBottom: 10,
                }}
              />
              <div style={{ fontSize: 12, color: T.textMuted }}>Loading {subMenu} events...</div>
            </div>
          ) : subMenu !== "all" && error[subMenu] ? (
            <div style={{ padding: 30, textAlign: "center" }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>📅</div>
              <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 15 }}>{error[subMenu]}</div>
              <button
                onClick={() => fetchEvents(subMenu)}
                style={{
                  padding: "6px 12px",
                  background: T.primary,
                  color: T.white,
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                  fontSize: 12,
                }}
              >
                Retry
              </button>
            </div>
          ) : subMenu === "all" ? (
            <>
              {events.upcoming?.length > 0 && (
                <>
                  <div
                    style={{
                      padding: "8px 16px",
                      background: `${T.primary}10`,
                      borderBottom: `1px solid ${T.borderLight}`,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: T.primary,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      🚀 Upcoming Events ({events.upcoming.length})
                    </div>
                  </div>
                  {events.upcoming.map((event) => {
                    const eventSlug = createEventSlug(event);
                    return (
                      <div
                        key={event._id}
                        onClick={() => handleEventClick(eventSlug)}
                        style={{
                          display: "block",
                          padding: "12px 16px",
                          textDecoration: "none",
                          borderBottom: `1px solid ${T.borderLight}`,
                          transition: "background 0.2s ease",
                          cursor: "pointer",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = T.offWhite)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: T.text, marginBottom: 6 }}>
                            {event.title}
                          </div>
                          <RiExternalLinkLine size={12} style={{ color: T.primary, opacity: 0.7 }} />
                        </div>
                        {event.subtitle && (
                          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6 }}>
                            {event.subtitle}
                          </div>
                        )}
                        <div
                          style={{
                            fontSize: 11,
                            color: T.textMuted,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          <RiCalendarLine size={12} style={{ color: T.primary }} />
                          <span>{new Date(event.date).toLocaleDateString()}</span>
                          {event.location && (
                            <>
                              <span style={{ color: T.border }}>•</span>
                              <RiMapPinLine size={12} style={{ color: T.primary }} />
                              <span>{event.location}</span>
                            </>
                          )}
                          {event.type && (
                            <>
                              <span style={{ color: T.border }}>•</span>
                              <span
                                style={{
                                  background: `${T.primary}10`,
                                  padding: "2px 6px",
                                  borderRadius: 4,
                                  fontSize: 10,
                                  color: T.primary,
                                }}
                              >
                                {event.type}
                              </span>
                            </>
                          )}
                        </div>
                        {event.featured && (
                          <div style={{ marginTop: 6, fontSize: 10, color: T.accentGold, fontWeight: 600 }}>
                            ⭐ Featured Event
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
              {events.completed?.length > 0 && (
                <>
                  <div
                    style={{
                      padding: "8px 16px",
                      background: `${T.textMuted}10`,
                      borderBottom: `1px solid ${T.borderLight}`,
                      borderTop: `1px solid ${T.borderLight}`,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: T.textMuted,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      ✅ Completed Events ({events.completed.length})
                    </div>
                  </div>
                  {events.completed.map((event) => {
                    const eventSlug = createEventSlug(event);
                    return (
                      <div
                        key={event._id}
                        onClick={() => handleEventClick(eventSlug)}
                        style={{
                          display: "block",
                          padding: "12px 16px",
                          textDecoration: "none",
                          borderBottom: `1px solid ${T.borderLight}`,
                          transition: "background 0.2s ease",
                          cursor: "pointer",
                          opacity: 0.8,
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = T.offWhite)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: T.text, marginBottom: 6 }}>
                            {event.title}
                          </div>
                          <RiExternalLinkLine size={12} style={{ color: T.textMuted, opacity: 0.5 }} />
                        </div>
                        {event.subtitle && (
                          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6 }}>
                            {event.subtitle}
                          </div>
                        )}
                        <div
                          style={{
                            fontSize: 11,
                            color: T.textMuted,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          <RiCalendarLine size={12} style={{ color: T.textMuted }} />
                          <span>{new Date(event.date).toLocaleDateString()}</span>
                          {event.location && (
                            <>
                              <span style={{ color: T.border }}>•</span>
                              <RiMapPinLine size={12} style={{ color: T.textMuted }} />
                              <span>{event.location}</span>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
              {(!events.upcoming || events.upcoming.length === 0) &&
                (!events.completed || events.completed.length === 0) && (
                  <div style={{ padding: 40, textAlign: "center" }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>📅</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 4 }}>
                      No Events Found
                    </div>
                    <div style={{ fontSize: 12, color: T.textMuted }}>
                      Check back later for upcoming events
                    </div>
                  </div>
                )}
              {(events.upcoming?.length > 0 || events.completed?.length > 0) && (
                <Link
                  to="/events"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    padding: "12px 16px",
                    margin: "12px 12px 16px",
                    background: T.gradPrimary,
                    color: T.white,
                    textDecoration: "none",
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    transition: "all 0.2s ease",
                    boxShadow: T.shadow,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = "0.9";
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = T.shadowMd;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = "1";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = T.shadow;
                  }}
                >
                  <RiEyeLine size={14} />
                  View All Events
                  <RiArrowRightLine size={12} />
                </Link>
              )}
            </>
          ) : subMenu !== "all" && events[subMenu]?.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center" }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>✨</div>
              <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 5 }}>
                No {subMenu} events available
              </div>
              <div style={{ fontSize: 11, color: T.textMuted }}>
                Check back later for {subMenu === "upcoming" ? "new events" : "past event archives"}
              </div>
            </div>
          ) : (
            subMenu !== "all" && (
              <>
                {events[subMenu]?.map((event) => {
                  const eventSlug = createEventSlug(event);
                  return (
                    <div
                      key={event._id}
                      onClick={() => handleEventClick(eventSlug)}
                      style={{
                        display: "block",
                        padding: "12px 16px",
                        textDecoration: "none",
                        borderBottom: `1px solid ${T.borderLight}`,
                        transition: "background 0.2s ease",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = T.offWhite)}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: T.text, marginBottom: 6 }}>
                          {event.title}
                        </div>
                        <RiExternalLinkLine size={12} style={{ color: T.primary, opacity: 0.7 }} />
                      </div>
                      {event.subtitle && (
                        <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6 }}>
                          {event.subtitle}
                        </div>
                      )}
                      <div
                        style={{
                          fontSize: 11,
                          color: T.textMuted,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <RiCalendarLine size={12} style={{ color: T.primary }} />
                        <span>{new Date(event.date).toLocaleDateString()}</span>
                        {event.location && (
                          <>
                            <span style={{ color: T.border }}>•</span>
                            <RiMapPinLine size={12} style={{ color: T.primary }} />
                            <span>{event.location}</span>
                          </>
                        )}
                        {event.type && (
                          <>
                            <span style={{ color: T.border }}>•</span>
                            <span
                              style={{
                                background: `${T.primary}10`,
                                padding: "2px 6px",
                                borderRadius: 4,
                                fontSize: 10,
                                color: T.primary,
                              }}
                            >
                              {event.type}
                            </span>
                          </>
                        )}
                      </div>
                      {event.featured && (
                        <div style={{ marginTop: 6, fontSize: 10, color: T.accentGold, fontWeight: 600 }}>
                          ⭐ Featured Event
                        </div>
                      )}
                    </div>
                  );
                })}
                {subMenu === "completed" && events.completed?.length > 0 && (
                  <Link
                    to="/events"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      padding: "12px 16px",
                      margin: "8px 12px 12px",
                      background: T.gradPrimary,
                      color: T.white,
                      textDecoration: "none",
                      borderRadius: 8,
                      fontSize: 12,
                      fontWeight: 600,
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = "0.9";
                      e.currentTarget.style.transform = "translateY(-1px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = "1";
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    <RiEyeLine size={14} />
                    View All Events
                    <RiArrowRightLine size={12} />
                  </Link>
                )}
              </>
            )
          )}
        </div>
      </div>
      <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
    </DropdownShell>
  );
}

/* ─────────────────────────────────────────────────────────────
   MOBILE MENU — MAIN SITE (with icons)
───────────────────────────────────────────────────────────── */
function MobileMenu({ isOpen, onClose }) {
  if (!isOpen) return null;
  const linkStyle = (indent = false) => ({
    padding: indent ? "8px 20px 8px 36px" : "14px 20px",
    textDecoration: "none",
    fontSize: indent ? 14 : 16,
    fontWeight: indent ? 400 : 500,
    color: "#000",
    borderBottom: `1px solid ${BORDER}`,
    display: "flex",
    alignItems: "center",
    gap: 8,
  });
  return (
    <motion.div
      initial={{ opacity: 0, x: "100%" }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: "100%" }}
      transition={{ type: "tween", duration: 0.3 }}
      className="gnav-mobile-menu"
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
        <Link to="/" onClick={onClose} style={linkStyle()}>
          <RiHomeLine size={18} /> Home
        </Link>
        <Link to="/about" onClick={onClose} style={linkStyle()}>
          <RiTeamLine size={18} /> About Us
        </Link>
        <div style={{ ...linkStyle(), color: "#000" }}>
          <RiFileTextFill size={18} /> Policy
        </div>
        <Link to="/registration-policies" onClick={onClose} style={linkStyle(true)}>
          <RiFileListLine size={16} /> Registration Policy
        </Link>
        <Link to="/privacy-policy" onClick={onClose} style={linkStyle(true)}>
          <RiShieldStarLine size={16} /> Privacy Policy
        </Link>
        <Link to="/code-of-conduct" onClick={onClose} style={linkStyle()}>
          <RiShieldCheckLine size={18} /> Code of Conduct
        </Link>
        <Link to="/contact" onClick={onClose} style={linkStyle()}>
          <RiMailLine size={18} /> Contact Us
        </Link>
      </div>
    </motion.div>
  );
}

/* ─────────────────────────────────────────────────────────────
   MOBILE MENU — SUMMIT (with parameterized Contact link and icons)
───────────────────────────────────────────────────────────── */
function SummitMobileMenu({ isOpen, onClose, summitTitle, summitSlug }) {
  if (!isOpen) return null;
  const base = `/event/${summitSlug}`;
  const cur = window.location.pathname;

  const linkStyle = (path, indent = false) => ({
    padding: indent ? "8px 20px 8px 32px" : "14px 20px",
    textDecoration: "none",
    fontSize: indent ? 14 : 15,
    fontWeight: cur === path ? 700 : indent ? 400 : 500,
    color: cur === path ? ACCENT : "#000",
    background: cur === path ? "rgba(14,124,134,0.08)" : "transparent",
    borderLeft: cur === path ? `3px solid ${ACCENT}` : "3px solid transparent",
    display: "flex",
    alignItems: "center",
    gap: 8,
  });

  const sectionLabel = (text) => (
    <div className="section-label" style={{ padding: "10px 20px 3px", fontSize: 11, fontWeight: 700, color: "#666", letterSpacing: 0.5, textTransform: "uppercase" }}>
      {text}
    </div>
  );

  // Build the parameterized global contact URL
  const globalContactUrl = `/globalcontact?eventId=${encodeURIComponent(summitSlug)}&slug=${encodeURIComponent(summitSlug)}`;

  return (
    <motion.div
      initial={{ opacity: 0, x: "100%" }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: "100%" }}
      transition={{ type: "tween", duration: 0.3 }}
      className="gnav-mobile-menu"
    >
      <div style={{ padding: "0 16px 12px", borderBottom: `1px solid ${BORDER}` }}>
        <div
          style={{
            padding: "6px 12px",
            borderRadius: 20,
            background: "rgba(14,124,134,0.08)",
            color: ACCENT,
            fontSize: 12,
            fontWeight: 600,
            display: "inline-block",
          }}
        >
          <RiCalendarCheckLine style={{ marginRight: 5, verticalAlign: "middle" }} />
          {summitTitle}
        </div>
      </div>

      <Link to={base} onClick={onClose} style={linkStyle(base)} className="nav-link">
        <RiHomeLine size={16} /> Home
      </Link>
      <Link to={`${base}/committee`} onClick={onClose} style={linkStyle(`${base}/committee`)} className="nav-link">
        <RiGroupLine size={16} /> Committee
      </Link>

      {sectionLabel("Program")}
      <Link to={`${base}/speakers`} onClick={onClose} style={linkStyle(`${base}/speakers`, true)} className="nav-link-indent">
        <RiSpeakerLine size={14} /> Speakers
      </Link>
      <Link to={`${base}/programme-overview`} onClick={onClose} style={linkStyle(`${base}/programme-overview`, true)} className="nav-link-indent">
        <RiCalendarEventLine size={14} /> Preliminary Program
      </Link>
      <Link to={`${base}/schedule`} onClick={onClose} style={linkStyle(`${base}/schedule`, true)} className="nav-link-indent">
        <RiCalendarLine size={14} /> Schedule
      </Link>
      <Link to={`${base}/presentation-guidelines`} onClick={onClose} style={linkStyle(`${base}/presentation-guidelines`, true)} className="nav-link-indent">
        <RiPresentationLine size={14} /> Presentation Guidelines
      </Link>

      {sectionLabel("Submission")}
      <Link to={`${base}/topics`} onClick={onClose} style={linkStyle(`${base}/topics`, true)} className="nav-link-indent">
        <RiArticleLine size={14} /> Topics
      </Link>
      <Link to={`${base}/submission-guidelines`} onClick={onClose} style={linkStyle(`${base}/submission-guidelines`, true)} className="nav-link-indent">
        <RiFileCopyLine size={14} /> Submission Guidelines
      </Link>
      <Link to={`${base}/abstract-submission`} onClick={onClose} style={linkStyle(`${base}/abstract-submission`, true)} className="nav-link-indent">
        <RiUploadLine size={14} /> Submit Abstract
      </Link>

      {sectionLabel("Registration")}
      <Link to={`${base}/registration-fee`} onClick={onClose} style={linkStyle(`${base}/registration-fee`, true)} className="nav-link-indent">
        <RiFileListLine size={14} /> Registration Fee
      </Link>
      <Link to={`${base}/registration-policies`} onClick={onClose} style={linkStyle(`${base}/registration-policies`, true)} className="nav-link-indent">
        <RiShieldCheckLine size={14} /> Registration Policies
      </Link>
      <Link to={`${base}/register`} onClick={onClose} style={linkStyle(`${base}/register`, true)} className="nav-link-indent">
        <RiCheckLine size={14} /> Online Registration
      </Link>

      <Link to={`${base}/sponsors`} onClick={onClose} style={linkStyle(`${base}/sponsors`)} className="nav-link">
        <RiHandHeartLine size={16} /> Sponsors
      </Link>

      {sectionLabel("Information")}
      <Link to={`${base}/venue`} onClick={onClose} style={linkStyle(`${base}/venue`, true)} className="nav-link-indent">
        <RiMapPinLine size={14} /> Venue and Information
      </Link>
      <Link to={`${base}/awards`} onClick={onClose} style={linkStyle(`${base}/awards`, true)} className="nav-link-indent">
        <RiAwardLine size={14} /> Awards
      </Link>
      <Link to={`${base}/code-of-conduct`} onClick={onClose} style={linkStyle(`${base}/code-of-conduct`, true)} className="nav-link-indent">
        <RiShieldCheckLine size={14} /> Code of Conduct
      </Link>
      <Link to={`${base}/privacy-policy`} onClick={onClose} style={linkStyle(`${base}/privacy-policy`, true)} className="nav-link-indent">
        <RiShieldStarLine size={14} /> Privacy Policy
      </Link>
      <Link to={`${base}/summit-publications`} onClick={onClose} style={linkStyle(`${base}/summit-publications`, true)} className="nav-link-indent">
        <RiBookOpenLine size={14} /> Publications
      </Link>

      {/* ✅ Parameterized global contact link with icon */}
      <Link to={globalContactUrl} onClick={onClose} style={linkStyle("/globalcontact")} className="nav-link">
        <RiMailLine size={16} /> Contact
      </Link>
    </motion.div>
  );
}

/* ─────────────────────────────────────────────────────────────
   MAIN SITE NAVBAR
───────────────────────────────────────────────────────────── */
function MainSiteNavbar() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const isActive = (path) =>
    path === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(path);

  return (
    <>
      <style>{NAVBAR_CSS}</style>
      <nav
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: NAV_BG,
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        <div
          style={{
            height: 4,
            background: `linear-gradient(90deg, ${ACCENT} 0%, ${GOLD} 50%, ${ACCENT} 100%)`,
          }}
        />
        <div
          style={{
            maxWidth: 1600,
            margin: "0 auto",
            padding: "0 24px",
            height: 80,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <Link to="/" style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            <img src={logo} alt="logo" style={{ height: 150, width: "auto", objectFit: "contain" }} />
          </Link>

          <div
            className="gnav-desktop"
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <NavLink path="/" label="Home" icon={RiHomeLine} isActive={isActive("/")} />
            <NavLink path="/about" label="About Us" icon={RiTeamLine} isActive={isActive("/about")} />
            <MeetingsDropdown />
            <MainPolicyDropdown />
            <NavLink path="/code-of-conduct" label="Code of Conduct" icon={RiShieldCheckLine} isActive={isActive("/code-of-conduct")} />
            <NavLink path="/contact" label="Contact Us" icon={RiMailLine} isActive={isActive("/contact")} />
          </div>

          <HamburgerBtn isOpen={isOpen} onClick={() => setIsOpen((o) => !o)} />
        </div>
      </nav>

      <AnimatePresence>
        {isOpen && <MobileMenu isOpen={isOpen} onClose={() => setIsOpen(false)} />}
      </AnimatePresence>
      <div style={{ paddingTop: 80 }} />
    </>
  );
}

/* ─────────────────────────────────────────────────────────────
   SUMMIT PAGE NAVBAR - Event title removed, with global contact link
───────────────────────────────────────────────────────────── */
function SummitPageNavbar({ summitId, summitSlug }) {
  const location = useLocation();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const base = `/event/${summitSlug}`;

  const fetchEventData = async () => {
    setLoading(true);
    try {
      const timestamp = Date.now();
      const token = localStorage.getItem("token");
      let response = await fetch(`${API_BASE}/api/events/${summitId}?_=${timestamp}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      let data = await response.json();
      if (!data.success || !data.event) {
        response = await fetch(`${API_BASE}/api/events/id/${summitId}?_=${timestamp}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        data = await response.json();
      }
      if (data.success && data.event) {
        setEvent(data.event);
      } else if (data.event) {
        setEvent(data.event);
      } else {
        setEvent(null);
      }
    } catch (error) {
      console.error("❌ Error fetching event:", error);
      setEvent(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (summitId) {
      fetchEventData();
    }
  }, [summitId, location.pathname]);

  const effectiveSlug = event?.slug || summitSlug;
  const effectiveId = event?._id || summitId;

  const isActive = (path) =>
    path === base
      ? location.pathname === base
      : location.pathname.startsWith(path);

  // Build the global contact URL with event ID and slug as query params
  const globalContactUrl = `/globalcontact?eventId=${encodeURIComponent(effectiveId)}&slug=${encodeURIComponent(effectiveSlug)}`;

  return (
    <>
      <style>{NAVBAR_CSS}</style>
      <nav
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: NAV_BG,
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        <div
          style={{
            height: 4,
            background: `linear-gradient(90deg, ${ACCENT} 0%, ${GOLD} 50%, ${ACCENT} 100%)`,
          }}
        />
        <div
          style={{
            maxWidth: 1600,
            margin: "0 auto",
            padding: "0 24px",
            height: 80,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <Link to="/" style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            <img src={logo} alt="logo" style={{ height: 150, width: "auto", objectFit: "contain" }} />
          </Link>

          <div
            className="gnav-desktop"
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <NavLink key="home" path={base} label="Home" icon={RiHomeLine} isActive={isActive(base)} />
            <NavLink key="committee" path={`${base}/committee`} label="Committee" icon={RiGroupLine} isActive={isActive(`${base}/committee`)} />
            <ProgramDropdown summitId={effectiveId} summitSlug={effectiveSlug} />
            <SubmissionDropdown summitId={effectiveId} summitSlug={effectiveSlug} />
            <RegistrationDropdown summitId={effectiveId} summitSlug={effectiveSlug} />
            <NavLink key="sponsors" path={`${base}/sponsors`} label="Sponsors" icon={RiHandHeartLine} isActive={isActive(`${base}/sponsors`)} />
            <InformationDropdown summitId={effectiveId} summitSlug={effectiveSlug} />
            <NavLink key="globalcontact" path={globalContactUrl} label="Contact" icon={RiMailLine} isActive={location.pathname === "/globalcontact"} />
          </div>

          <HamburgerBtn isOpen={isOpen} onClick={() => setIsOpen((o) => !o)} />
        </div>
      </nav>

      <AnimatePresence>
        {isOpen && event && (
          <SummitMobileMenu
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            summitTitle={event.title}
            summitSlug={effectiveSlug}
          />
        )}
      </AnimatePresence>
      <div style={{ paddingTop: 80 }} />
    </>
  );
}

/* ─────────────────────────────────────────────────────────────
   HAMBURGER
───────────────────────────────────────────────────────────── */
function HamburgerBtn({ isOpen, onClick }) {
  return (
    <button
      className="gnav-hamburger"
      onClick={onClick}
      style={{
        marginLeft: "auto",
        padding: 10,
        borderRadius: 8,
        border: `1px solid ${BORDER}`,
        background: "#fff",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {isOpen ? <RiCloseLine size={24} /> : <RiMenuLine size={24} />}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────
   GLOBAL NAVBAR — with query param support for event context
───────────────────────────────────────────────────────────── */
export default function GlobalNavbar() {
  const location = useLocation();

  // ─── 1. Try to get identifier from path ────────────────────
  const eventMatch = location.pathname.match(/^\/event\/([^/]+)/);
  const agendaMatch = location.pathname.match(/^\/agenda\/([^/]+)/);
  let identifier = eventMatch ? eventMatch[1] : (agendaMatch ? agendaMatch[1] : null);

  // ─── 2. If not found in path, try query params ─────────────
  const searchParams = new URLSearchParams(location.search);
  const queryEventId = searchParams.get('eventId');
  const querySlug = searchParams.get('slug');

  if (!identifier && queryEventId) {
    identifier = queryEventId;
  } else if (!identifier && querySlug) {
    identifier = querySlug;
  }
  if (identifier) {
    return <SummitPageNavbar summitId={identifier} summitSlug={querySlug || identifier} />;
  }
  return <MainSiteNavbar />;
}